import React, { useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip as ChartJsTooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format } from "date-fns";
import { Card, CardHeader, CardBody, Chip, Tooltip, Avatar, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@heroui/react";
import { Link } from "react-router-dom";
import { formatCurrency } from "shared/utils/currency";
import { RenovationProject, ProjectDateType } from "shared/types/renovation";
import { useUsers, useActiveUsers } from "shared/contexts/users.context";
import { UserSelection } from "shared/components/userSelection/userSelection";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";

type Props = {
  projects: RenovationProject[];
  year: number;
};

// Local helpers (duplicated from renovation.tsx to keep this component self-contained)
const getStartDate = (project: RenovationProject): Date | null => {
  const actualStart = project.dates?.find(
    d => d.type === ProjectDateType.ACTUAL_START && !d.isDeprecated
  );
  const expectedStart = project.dates?.find(
    d => d.type === ProjectDateType.EXPECTED_START && !d.isDeprecated
  );
  return actualStart ? new Date(actualStart.date) : expectedStart ? new Date(expectedStart.date) : null;
};

const getEndDate = (project: RenovationProject): Date | null => {
  const actualEnd = project.dates?.find(
    d => d.type === ProjectDateType.ACTUAL_END && !d.isDeprecated
  );
  const expectedEnd = project.dates?.find(
    d => d.type === ProjectDateType.EXPECTED_END && !d.isDeprecated
  );
  return actualEnd ? new Date(actualEnd.date) : expectedEnd ? new Date(expectedEnd.date) : null;
};

const monthLabels = ["Jan", "Feb", "Mrt", "Apr", "Mei", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];

const clampToYear = (date: Date, year: number) => {
  const jan1 = new Date(year, 0, 1);
  const dec31 = new Date(year, 11, 31, 23, 59, 59, 999);
  if (date < jan1) return jan1;
  if (date > dec31) return dec31;
  return date;
};

const getProjectTimeline = (project: RenovationProject, year: number) => {
  // Prefer project-level dates; otherwise, infer from parts' planned dates if available
  let start = getStartDate(project);
  let end = getEndDate(project);

  if (!start || !end) {
    const partStarts = (project.parts ?? [])
      .map(p => (p.plannedStartDate ? new Date(p.plannedStartDate) : null))
      .filter((d): d is Date => !!d);
    const partEnds = (project.parts ?? [])
      .map(p => (p.plannedEndDate ? new Date(p.plannedEndDate) : null))
      .filter((d): d is Date => !!d);
    if (!start && partStarts.length) start = new Date(Math.min(...partStarts.map(d => d.getTime())));
    if (!end && partEnds.length) end = new Date(Math.max(...partEnds.map(d => d.getTime())));
  }

  if (!start && !end) return null;
  // Ensure project timeline overlaps with selected year; otherwise, don't render it
  const jan1 = new Date(year, 0, 1);
  const dec31 = new Date(year, 11, 31, 23, 59, 59, 999);
  const rawStart = start as Date;
  const rawEnd = end as Date;
  if (rawEnd < jan1 || rawStart > dec31) return null;

  // If only one side is known, approximate to the year bounds for visualization
  start = start ? clampToYear(start, year) : new Date(year, 0, 1);
  end = end ? clampToYear(end, year) : new Date(year, 11, 31);

  const startMonth = start.getFullYear() === year ? start.getMonth() : 0;
  const endMonth = end.getFullYear() === year ? end.getMonth() : 11;
  return { start, end, startMonth, endMonth };
};

// Budget now comes from investment item part assignments
const getPartBudget = (part: any): number => {
  return part?.investmentAssignments?.reduce((sum: number, assignment: any) => sum + Number(assignment.assignedAmount || 0), 0) || 0;
};

const getProjectBudget = (project: RenovationProject): number => {
  return (project.parts ?? []).reduce((sum, part: any) => sum + getPartBudget(part), 0);
};

// Helper to detect if a project started in a previous year but continues into the current year
const getProjectContinuationInfo = (project: RenovationProject, year: number) => {
  const startDate = getStartDate(project);
  const endDate = getEndDate(project);
  
  if (!startDate || !endDate) return null;
  
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  
  // Project started before the current year and continues into or beyond it
  if (startYear < year && endYear >= year) {
    const yearsRunning = year - startYear;
    return {
      startYear,
      yearsRunning,
      isOngoing: endYear > year,
      message: `Project gestart in ${startYear}, loopt door in ${year}${yearsRunning > 1 ? ` (+${yearsRunning} jaar)` : ''}`
    };
  }
  
  return null;
};

const PlanningView: React.FC<Props> = ({ projects, year }) => {
  const monthGrid = useMemo(() => monthLabels.map((m, i) => ({ label: m, index: i })), []);
  const { users } = useUsers();
  const { users: activeUsers } = useActiveUsers();
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignProjectId, setAssignProjectId] = useState<number | null>(null);
  const [localAssignments, setLocalAssignments] = useState<Record<number, string | undefined>>({});
  const { session } = useSession();
  const currentAssignedUid = useMemo(() => {
    if (!assignProjectId) return undefined;
    const p = projects.find(pr => pr.id === assignProjectId);
    return localAssignments[assignProjectId] ?? (p as any)?.responsibleUserId;
  }, [assignProjectId, projects, localAssignments]);

  // 30/40/30 heuristic distribution of a part's budget across months within the selected year
  const distributeBudgetByMonths = (part: any, y: number): number[] => {
    const budget = getPartBudget(part);
    const result = Array(12).fill(0);
    if (!budget || budget <= 0) return result;

    // Use raw dates to determine overlap with selected year
    const psRaw = part?.plannedStartDate ? new Date(part.plannedStartDate) : null;
    const peRaw = part?.plannedEndDate ? new Date(part.plannedEndDate) : null;
    if (!psRaw || !peRaw) return result;

    const jan1 = new Date(y, 0, 1);
    const dec31 = new Date(y, 11, 31, 23, 59, 59, 999);

    // Skip parts that do not overlap the selected year at all
    if (peRaw < jan1 || psRaw > dec31) return result;

    // Clamp to the selected year for distribution
    const ps = clampToYear(psRaw, y);
    const pe = clampToYear(peRaw, y);
    if (!ps || !pe || pe < ps) return result;

    const startMonth = ps.getFullYear() === y ? ps.getMonth() : 0;
    const endMonth = pe.getFullYear() === y ? pe.getMonth() : 11;
    const span = endMonth - startMonth + 1;
    if (span <= 0) return result;

    if (span === 1) {
      result[startMonth] += budget; // all in one month (start=end)
      return result;
    }
    if (span === 2) {
      result[startMonth] += budget * 0.5;
      result[endMonth] += budget * 0.5;
      return result;
    }

    const startPortion = budget * 0.3;
    const endPortion = budget * 0.3;
    const middleMonths = span - 2;
    const middlePer = (budget * 0.4) / middleMonths;
    result[startMonth] += startPortion;
    for (let m = startMonth + 1; m <= endMonth - 1; m++) result[m] += middlePer;
    result[endMonth] += endPortion;
    return result;
  };

  // Board metrics: running projects count and monthly cash plan
  const { monthlyRunning, monthlyCash } = useMemo(() => {
    const running = Array(12).fill(0);
    const cash = Array(12).fill(0);

    projects.forEach(project => {
      const timeline = getProjectTimeline(project, year);
      if (timeline) {
        for (let m = timeline.startMonth; m <= timeline.endMonth; m++) running[m] += 1;
      }
      (project.parts ?? []).forEach((part: any) => {
        const dist = distributeBudgetByMonths(part, year);
        for (let m = 0; m < 12; m++) cash[m] += dist[m];
      });
    });

    return { monthlyRunning: running, monthlyCash: cash };
  }, [projects, year]);

  // Helper removed (no longer using cash for workload)

  // Determine months covered by a part or fallback to project timeline
  const getPartMonths = (part: any, _project: RenovationProject, y: number): number[] => {
    const months: number[] = [];
    // Use only part-level planned dates for workload. If missing, skip.
    if (!part?.plannedStartDate || !part?.plannedEndDate) return months;
    const start = new Date(part.plannedStartDate);
    const end = new Date(part.plannedEndDate);
    if (!start || !end) return months;
    const s = clampToYear(start, y);
    const e = clampToYear(end, y);
    if (e < s) return months;
    const sM = s.getFullYear() === y ? s.getMonth() : 0;
    const eM = e.getFullYear() === y ? e.getMonth() : 11;
    for (let m = sM; m <= eM; m++) months.push(m);
    return months;
  };

  // Workload aggregation per user per month (counts of parts)
  const workload = useMemo(() => {
    type UserAgg = {
      id: string;
      name: string;
      total: number; // total parts across months
      monthly: number[]; // parts per month
      image?: string;
    };

    const byUser: Record<string, UserAgg> = {};
    const ensure = (id: string): UserAgg => {
      if (!byUser[id]) {
        const user = users.find((u: any) => u.id === id);
        byUser[id] = {
          id,
          name: user ? `${user.first_name} ${user.last_name}` : (id === 'unassigned' ? 'Niet toegewezen' : id),
          image: user?.image_url,
          total: 0,
          monthly: Array(12).fill(0)
        };
      }
      return byUser[id];
    };

    projects.forEach(project => {
      const assignedId = localAssignments[project.id] ?? (project as any).responsibleUserId ?? 'unassigned';
      const agg = ensure(assignedId);
      (project.parts ?? []).forEach((part: any) => {
        const months = getPartMonths(part, project, year);
        months.forEach(m => {
          agg.monthly[m] += 1;
          agg.total += 1;
        });
      });
    });

    const usersList = Object.values(byUser).sort((a, b) => b.total - a.total);
    const topK = Math.min(usersList.length <= 10 ? usersList.length : 10, usersList.length);
    const top = usersList.slice(0, topK);
    const rest = usersList.slice(topK);
    const others: UserAgg | null = rest.length > 0 ? {
      id: 'overig', name: 'Overig', image: undefined,
      total: rest.reduce((s, u) => s + u.total, 0),
      monthly: rest.reduce((arr, u) => arr.map((v, i) => v + u.monthly[i]), Array(12).fill(0))
    } : null;

    return { usersList, top, others };
  }, [projects, users, localAssignments, year]);

  // Monthly number of project parts with planned cash distribution in that month
  const monthlyParts = useMemo(() => {
    const parts = Array(12).fill(0);
    projects.forEach(project => {
      (project.parts ?? []).forEach((part: any) => {
        const dist = distributeBudgetByMonths(part, year);
        dist.forEach((amount: number, idx: number) => {
          if (amount > 0) parts[idx] += 1;
        });
      });
    });
    return parts;
  }, [projects, year]);

  // Details per maand: welke projectonderdelen dragen bij (voor tooltips)
  const monthlyPartDetails = useMemo(() => {
    const details: Array<Array<{ projectId: number; partId: number; title: string; amount: number }>> =
      Array.from({ length: 12 }, () => []);

    projects.forEach(project => {
      (project.parts ?? []).forEach((part: any) => {
        const dist = distributeBudgetByMonths(part, year);
        dist.forEach((amount: number, idx: number) => {
          if (amount > 0) {
            details[idx].push({
              projectId: project.id,
              partId: part.id,
              title: part.title,
              amount
            });
          }
        });
      });
    });

    return details.map(list => list.sort((a, b) => b.amount - a.amount));
  }, [projects, year]);

  const [selectedMonthIdx, setSelectedMonthIdx] = useState<number | null>(null);

  const getProjectMonthAmount = (project: RenovationProject, monthIdx: number): number => {
    return (project.parts ?? []).reduce((sum, part: any) => sum + distributeBudgetByMonths(part, year)[monthIdx], 0);
  };

  const getProjectPartsForMonth = (project: RenovationProject, monthIdx: number) => {
    const parts = (project.parts ?? []).map((part: any) => ({
      part,
      amount: distributeBudgetByMonths(part, year)[monthIdx]
    })).filter(p => p.amount > 0)
      .sort((a, b) => b.amount - a.amount);
    return parts;
  };

  // Nieuwe helper: bereken uitsplitsing per project voor de geselecteerde maand
  const getProjectMonthBreakdown = (project: RenovationProject, monthIdx: number) => {
    const parts = project.parts ?? [];
    // Maandelijkse bijdragen
    let singleMonthSum = 0;
    let twoMonthsStartSum = 0;
    let twoMonthsEndSum = 0;
    let voorschotSum = 0;
    let progressSum = 0;
    let settlementSum = 0;

    // Basis (totaal budget) per categorie voor deze maand
    let singleBase = 0;
    let twoStartBase = 0;
    let twoEndBase = 0;
    let advanceBase = 0;
    let progressBase = 0;
    let settlementBase = 0;

    parts.forEach((part: any) => {
      const budget = getPartBudget(part);
      if (!budget || budget <= 0) return;

      const psRaw = part?.plannedStartDate ? new Date(part.plannedStartDate) : null;
      const peRaw = part?.plannedEndDate ? new Date(part.plannedEndDate) : null;
      if (!psRaw || !peRaw || peRaw < psRaw) return;

      const ps = clampToYear(psRaw, year);
      const pe = clampToYear(peRaw, year);
      const startM = ps.getFullYear() === year ? ps.getMonth() : 0;
      const endM = pe.getFullYear() === year ? pe.getMonth() : 11;
      const span = endM - startM + 1;

      if (span <= 0) return;

      if (span === 1) {
        if (monthIdx === startM) {
          singleMonthSum += budget;
          singleBase += budget;
        }
        return;
      }

      if (span === 2) {
        if (monthIdx === startM) {
          twoMonthsStartSum += budget * 0.5;
          twoStartBase += budget;
        }
        if (monthIdx === endM) {
          twoMonthsEndSum += budget * 0.5;
          twoEndBase += budget;
        }
        return;
      }

      if (monthIdx === startM) {
        voorschotSum += budget * 0.3;
        advanceBase += budget;
        return;
      }
      if (monthIdx === endM) {
        settlementSum += budget * 0.3;
        settlementBase += budget;
        return;
      }

      if (monthIdx > startM && monthIdx < endM) {
        const middleMonths = span - 2;
        if (middleMonths > 0) {
          progressSum += (budget * 0.4) / middleMonths;
          progressBase += budget;
        }
      }
    });

    const items: { key: string; label: string; amount: number; totalRef?: number }[] = [];
    if (singleMonthSum > 0) items.push({ key: 'single', label: '100% in één maand', amount: singleMonthSum, totalRef: singleBase });
    if (twoMonthsStartSum > 0) items.push({ key: 'two-start', label: 'Voorschot (2 maanden) 50% van totaalbudget', amount: twoMonthsStartSum, totalRef: twoStartBase });
    if (voorschotSum > 0) items.push({ key: 'advance', label: 'Voorschot 30% van totaalbudget', amount: voorschotSum, totalRef: advanceBase });
    if (progressSum > 0) items.push({ key: 'progress', label: 'Voortgang pro rata (40% verdeeld over tussenliggende maanden)', amount: progressSum, totalRef: progressBase });
    if (twoMonthsEndSum > 0) items.push({ key: 'two-end', label: 'Afrekening (2 maanden) 50% van totaalbudget', amount: twoMonthsEndSum, totalRef: twoEndBase });
    if (settlementSum > 0) items.push({ key: 'settlement', label: 'Afrekening 30% van totaalbudget', amount: settlementSum, totalRef: settlementBase });

    return items;
  };

  // Compute full project cash planning schedule for the selected year
  const getProjectCashSchedule = (project: RenovationProject, y: number) => {
    const schedule = {
      advance: new Map<number, number>(), // 30%
      progress: new Map<number, number>(), // 40% divided
      settlement: new Map<number, number>(), // 30%
      single: new Map<number, number>(), // 100% in one month
      twoStart: new Map<number, number>(), // 50%
      twoEnd: new Map<number, number>(), // 50%
      progressBase: new Map<number, number>() // base budgets driving progress per month
    } as const;

    (project.parts ?? []).forEach((part: any) => {
      const budget = getPartBudget(part);
      if (!budget || budget <= 0) return;

      const psRaw = part?.plannedStartDate ? new Date(part.plannedStartDate) : null;
      const peRaw = part?.plannedEndDate ? new Date(part.plannedEndDate) : null;
      if (!psRaw || !peRaw || peRaw < psRaw) return;

      // Skip parts that do not overlap this year
      const jan1 = new Date(y, 0, 1);
      const dec31 = new Date(y, 11, 31, 23, 59, 59, 999);
      if (peRaw < jan1 || psRaw > dec31) return;

      const ps = clampToYear(psRaw, y);
      const pe = clampToYear(peRaw, y);
      const startM = ps.getMonth();
      const endM = pe.getMonth();
      const span = endM - startM + 1;
      if (span <= 0) return;

      if (span === 1) {
        schedule.single.set(startM, (schedule.single.get(startM) || 0) + budget);
        return;
      }

      if (span === 2) {
        schedule.twoStart.set(startM, (schedule.twoStart.get(startM) || 0) + budget * 0.5);
        schedule.twoEnd.set(endM, (schedule.twoEnd.get(endM) || 0) + budget * 0.5);
        return;
      }

      // > 2 months: 30/40/30
      schedule.advance.set(startM, (schedule.advance.get(startM) || 0) + budget * 0.3);
      schedule.settlement.set(endM, (schedule.settlement.get(endM) || 0) + budget * 0.3);
      const middleMonths = span - 2;
      if (middleMonths > 0) {
        const per = (budget * 0.4) / middleMonths;
        for (let m = startM + 1; m <= endM - 1; m++) {
          schedule.progress.set(m, (schedule.progress.get(m) || 0) + per);
          schedule.progressBase.set(m, (schedule.progressBase.get(m) || 0) + budget);
        }
      }
    });

    // Convert maps to arrays sorted by month index
    const sortEntries = (map: Map<number, number>) => Array.from(map.entries()).sort((a, b) => a[0] - b[0]);

    return {
      advance: sortEntries(schedule.advance),
      progress: sortEntries(schedule.progress),
      settlement: sortEntries(schedule.settlement),
      single: sortEntries(schedule.single),
      twoStart: sortEntries(schedule.twoStart),
      twoEnd: sortEntries(schedule.twoEnd),
      progressBase: sortEntries(schedule.progressBase)
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-neutral-primary">Projectplanning {year}</h2>
        <div className="flex items-center gap-2 text-sm">
          <Chip className="bg-moss-primary text-white">Begroot</Chip>
          <Chip className="bg-brick-primary text-white">Geen budget</Chip>
        </div>
      </div>

      {/* Bestuursmetrics: Cashplanning en projecten per maand */}
      <Card className="p-4">
        <CardHeader className="flex items-center justify-between pb-2">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-neutral-primary">Cashplanning & projecten per maand</div>
            <Tooltip
              content={
                <div className="max-w-xs text-[12px] leading-relaxed">
                  <div className="font-semibold mb-1">Verdelingslogica</div>
                  <ul className="list-disc pl-4 space-y-1">
                    <li><span className="font-medium">30% voorschot</span> in de eerste maand.</li>
                    <li><span className="font-medium">40% tijdens het project</span>, gelijkmatig verdeeld over de tussenliggende maanden (excl. eerste en laatste maand).</li>
                    <li><span className="font-medium">30% afrekening</span> in de laatste maand.</li>
                  </ul>
                  <div className="mt-2 text-neutral-600">
                    Randgevallen:
                    <ul className="list-disc pl-4 mt-1 space-y-1">
                      <li><span className="font-medium">1 maand</span>: 100% in die maand.</li>
                      <li><span className="font-medium">2 maanden</span>: 50% in de eerste en 50% in de laatste maand.</li>
                    </ul>
                  </div>
                  <div className="mt-2 text-neutral-600">De verdeling gebeurt per onderdeel en wordt op projectniveau opgeteld. Data buiten het gekozen jaar worden begrensd naar jan/dec.</div>
                </div>
              }
            >
              <span className="text-[11px] text-neutral-secondary-mid underline decoration-dotted cursor-help">Uitleg cashplanning</span>
            </Tooltip>
          </div>
          <div className="text-xs text-neutral-600">
            {(() => {
              const meiIndex = 4; // 0-based (Jan=0)
              return (
                <span>
                  Mei: <span className="font-semibold text-neutral-primary">{monthlyRunning[meiIndex]} projecten</span> • Benodigd budget: <span className="font-semibold text-moss-primary">{formatCurrency(monthlyCash[meiIndex])}</span>
                </span>
              );
            })()}
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 xl:grid-cols-12 gap-3">
            {monthGrid.map(({ index, label }) => (
              <div
                key={index}
                className={`rounded-lg border p-3 bg-white flex flex-col gap-1 cursor-pointer transition-colors ${
                  selectedMonthIdx === index ? 'border-moss-primary ring-1 ring-moss-primary bg-moss-primary/5' : 'border-neutral-200 hover:bg-neutral-50'
                }`}
                onClick={() => setSelectedMonthIdx(prev => prev === index ? null : index)}
                aria-pressed={selectedMonthIdx === index}
                role="button"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-500">{label}</span>
                  <Chip size="sm" className="bg-moss-primary/10 text-moss-primary">
                    {monthlyRunning[index]}
                  </Chip>
                </div>
                <div className="text-[11px] text-neutral-500">Projecten</div>
                <div className="text-sm font-semibold text-neutral-primary mt-1">
                  {formatCurrency(monthlyCash[index])}
                </div>
                <div className="text-[11px] text-neutral-500">Benodigd budget</div>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {selectedMonthIdx !== null && (
        <>
        <Card className="p-4">
          <CardHeader className="flex items-center justify-between pb-2">
            <div className="text-sm font-medium text-neutral-primary">Details {monthLabels[selectedMonthIdx]}</div>
            <div className="text-xs text-neutral-600">
              {monthlyRunning[selectedMonthIdx]} projecten • Benodigd budget: <span className="font-semibold text-moss-primary">{formatCurrency(monthlyCash[selectedMonthIdx])}</span>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              {projects
                .filter(project => {
                  const tl = getProjectTimeline(project, year);
                  return tl ? (selectedMonthIdx! >= tl.startMonth && selectedMonthIdx! <= tl.endMonth) : false;
                })
                .map(project => {
                  const projectAmount = getProjectMonthAmount(project, selectedMonthIdx!);
                  const parts = getProjectPartsForMonth(project, selectedMonthIdx!);
                  const breakdownItems = getProjectMonthBreakdown(project, selectedMonthIdx!);
                  const assignedUid = localAssignments[project.id] ?? (project as any).responsibleUserId;
                  const assignedUser: any = users.find((u: any) => u.id === assignedUid);
                  const tooltipText = assignedUser ? `${assignedUser.first_name} ${assignedUser.last_name}` : 'Geen verantwoordelijke';
                  return (
                    <div key={project.id} className="rounded-lg border border-neutral-200 p-3 bg-white">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <Chip size="sm" className="bg-neutral-100 text-neutral-primary">{project.building?.id}</Chip>
                          <Link to={`/renovation/${project.id}?buildingId=${project.building?.id}`} className="text-moss-primary hover:underline font-medium truncate">
                            RENO-{project.id} {project.title}
                          </Link>
                          {/* Responsible avatar with quick assign */}
                          <Tooltip content={tooltipText}>
                            <button
                              className="rounded-full border border-neutral-primary/40 p-0.5 hover:bg-neutral-50"
                              onClick={(e) => { e.preventDefault(); setAssignProjectId(project.id); setAssignModalOpen(true); }}
                            >
                              {assignedUser?.image_url ? (
                                <Avatar size="sm" src={assignedUser.image_url} />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 text-sm">?</div>
                              )}
                            </button>
                          </Tooltip>
                        </div>
                        <div className="text-sm font-semibold text-neutral-primary">{formatCurrency(projectAmount)}</div>
                      </div>
                      {/* Uitleg uitsplitsing voor deze maand */}
                      {breakdownItems.length > 0 && (
                        <div className="mt-2 text-[12px] text-neutral-600">
                          <div className="font-medium text-neutral-primary">Uitsplitsing cashplanning deze maand</div>
                          <ul className="list-disc pl-5 mt-1 space-y-0.5">
                            {breakdownItems.map(item => (
                              <li key={item.key}>
                                {item.label}: <span className="font-semibold text-neutral-primary">{formatCurrency(item.amount)}</span>
                                {typeof item.totalRef === 'number' && (
                                  <span className="ml-1 text-neutral-secondary-mid">(van totaal {formatCurrency(item.totalRef)})</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {parts.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {parts.slice(0, 6).map(({ part, amount }) => (
                            <Tooltip key={part.id} content={`${part.title} • ${formatCurrency(amount)}`}>
                              <Chip size="sm" className="bg-moss-primary/10 text-moss-primary">
                                <span className="font-medium">RENO-{project.id}-O{part.id}</span>
                                <span className="mx-1">•</span>
                                <span className="truncate max-w-[160px]" title={part.title}>{part.title}</span>
                                {(part.plannedStartDate || part.plannedEndDate) && (
                                  <>
                                    <span className="mx-1">•</span>
                                    <span>{part.plannedStartDate ? format(new Date(part.plannedStartDate), 'dd/MM/yyyy') : '?'} → {part.plannedEndDate ? format(new Date(part.plannedEndDate), 'dd/MM/yyyy') : '?'}</span>
                                  </>
                                )}
                                <span className="mx-1">•</span>
                                <span>{formatCurrency(amount)}</span>
                              </Chip>
                            </Tooltip>
                          ))}
                          {parts.length > 6 && (
                            <Chip size="sm" className="bg-neutral-100 text-neutral-600">+{parts.length - 6} meer</Chip>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              {projects.filter(p => {
                const tl = getProjectTimeline(p, year);
                return tl ? (selectedMonthIdx! >= tl.startMonth && selectedMonthIdx! <= tl.endMonth) : false;
              }).length === 0 && (
                <div className="text-sm text-neutral-500">Geen projecten in {monthLabels[selectedMonthIdx]}.</div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Assign responsible modal */}
        <Modal isOpen={assignModalOpen} onClose={() => setAssignModalOpen(false)}>
          <ModalContent>
            <ModalHeader>
              <h3 className="text-lg font-medium">Verantwoordelijke toewijzen</h3>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-3">
                {activeUsers && activeUsers.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {activeUsers.map((u: any) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={async () => {
                          if (!assignProjectId) return;
                          setLocalAssignments(prev => ({ ...prev, [assignProjectId]: u.id }));
                          // Persist to backend
                          try {
                            await fetchWithAuth(
                              `${API_URL}/renovation/project/${assignProjectId}`,
                              { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ responsibleUserId: u.id }) },
                              session
                            );
                            toast.success(`Verantwoordelijke bijgewerkt naar ${u.first_name} ${u.last_name}`);
                          } catch {}
                          setAssignModalOpen(false);
                        }}
                        className={`flex items-center gap-2 p-2 rounded-md border text-left transition ${currentAssignedUid === u.id ? 'border-moss-primary bg-moss-primary/10' : 'border-neutral-primary/20 hover:border-moss-primary'}`}
                      >
                        <img src={u.image_url} alt={`${u.first_name} ${u.last_name}`} className="h-7 w-7 rounded-full object-cover" onError={(e: any) => { e.currentTarget.style.display='none'; }} />
                        {!u.image_url && <div className="h-7 w-7 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 text-xs">?</div>}
                        <span className="text-sm text-neutral-primary truncate">{u.first_name} {u.last_name}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-neutral-500">Geen gebruikers gevonden.</div>
                )}
                <div className="pt-2 border-t border-neutral-100">
                  <UserSelection defaultUser={null} onUserSelect={async (u: any) => {
                    if (!assignProjectId) return;
                    setLocalAssignments(prev => ({ ...prev, [assignProjectId]: u?.id }));
                    try {
                      await fetchWithAuth(
                        `${API_URL}/renovation/project/${assignProjectId}`,
                        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ responsibleUserId: u?.id }) },
                        session
                      );
                      if (u) {
                        toast.success(`Verantwoordelijke bijgewerkt naar ${u.first_name} ${u.last_name}`);
                      } else {
                        toast.success('Verantwoordelijke verwijderd');
                      }
                    } catch {}
                    setAssignModalOpen(false);
                  }} />
                </div>
                <div>
                  <Button variant="bordered" className="text-neutral-600" onPress={async () => {
                    if (!assignProjectId) return;
                    setLocalAssignments(prev => ({ ...prev, [assignProjectId]: undefined }));
                    try {
                      await fetchWithAuth(
                        `${API_URL}/renovation/project/${assignProjectId}`,
                        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ responsibleUserId: null }) },
                        session
                      );
                      toast.success('Verantwoordelijke verwijderd');
                    } catch {}
                    setAssignModalOpen(false);
                  }}>Geen verantwoordelijke</Button>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={() => setAssignModalOpen(false)}>Sluiten</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        </>
      )}

      {/* Bar chart: onderdelen vs cash per maand (nu onder de details) */}
      <Card className="p-4">
        <CardHeader className="pb-2">
          <div className="text-sm font-medium text-neutral-primary">Onderdelen en uitbetalingen per maand</div>
        </CardHeader>
        <CardBody>
          {(() => {
            // Register ChartJS (safe to call multiple times)
            ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartJsTooltip, Legend);

            // Prepare category totals for tooltip context (per month)
              const monthlyCategoryTotals = monthLabels.map((_, idx) => {
                // Aggregate over all projects/parts like in distributeBudgetByMonths
                let singleMonthSum = 0;
                let twoMonthsStartSum = 0;
                let twoMonthsEndSum = 0;
                let voorschotSum = 0;
                let progressSum = 0;
                let settlementSum = 0;

                // Bases for the hovered month
                let singleBase = 0;
                let twoStartBase = 0;
                let twoEndBase = 0;
                let advanceBase = 0;
                let progressBase = 0;
                let settlementBase = 0;

                projects.forEach(project => {
                  (project.parts ?? []).forEach((part: any) => {
                    const budget = getPartBudget(part);
                    if (!budget || budget <= 0) return;
                    const psRaw = part?.plannedStartDate ? new Date(part.plannedStartDate) : null;
                    const peRaw = part?.plannedEndDate ? new Date(part.plannedEndDate) : null;
                    if (!psRaw || !peRaw || peRaw < psRaw) return;
                    const ps = clampToYear(psRaw, year);
                    const pe = clampToYear(peRaw, year);
                    const startM = ps.getFullYear() === year ? ps.getMonth() : 0;
                    const endM = pe.getFullYear() === year ? pe.getMonth() : 11;
                    const span = endM - startM + 1;
                    if (span <= 0) return;

                    if (span === 1) {
                      if (idx === startM) {
                        singleMonthSum += budget;
                        singleBase += budget;
                      }
                      return;
                    }
                    if (span === 2) {
                      if (idx === startM) {
                        twoMonthsStartSum += budget * 0.5;
                        twoStartBase += budget;
                      }
                      if (idx === endM) {
                        twoMonthsEndSum += budget * 0.5;
                        twoEndBase += budget;
                      }
                      return;
                    }

                    // > 2 months
                    if (idx === startM) {
                      voorschotSum += budget * 0.3;
                      advanceBase += budget;
                      return;
                    }
                    if (idx === endM) {
                      settlementSum += budget * 0.3;
                      settlementBase += budget;
                      return;
                    }
                    if (idx > startM && idx < endM) {
                      const middleMonths = span - 2;
                      if (middleMonths > 0) {
                        progressSum += (budget * 0.4) / middleMonths;
                        progressBase += budget;
                      }
                    }
                  });
                });

                const items = [] as { key: string; label: string; amount: number; totalRef?: number }[];
                if (singleMonthSum > 0) items.push({ key: 'single', label: '100% in één maand', amount: singleMonthSum, totalRef: singleBase });
                if (twoMonthsStartSum > 0) items.push({ key: 'two-start', label: 'Voorschot (2 maanden) 50% van totaalbudget', amount: twoMonthsStartSum, totalRef: twoStartBase });
                if (voorschotSum > 0) items.push({ key: 'advance', label: 'Voorschot 30% van totaalbudget', amount: voorschotSum, totalRef: advanceBase });
                if (progressSum > 0) items.push({ key: 'progress', label: 'Voortgang pro rata (40% verdeeld over tussenliggende maanden)', amount: progressSum, totalRef: progressBase });
                if (twoMonthsEndSum > 0) items.push({ key: 'two-end', label: 'Afrekening (2 maanden) 50% van totaalbudget', amount: twoMonthsEndSum, totalRef: twoEndBase });
                if (settlementSum > 0) items.push({ key: 'settlement', label: 'Afrekening 30% van totaalbudget', amount: settlementSum, totalRef: settlementBase });

                return items;
              });

            const data = {
              labels: monthLabels,
                datasets: (() => {
                  // First dataset (parts count) remains as-is
                  const datasets: any[] = [
                    {
                      label: 'Onderdelen',
                      data: monthlyParts,
                      backgroundColor: 'rgba(49, 64, 59, 0.7)',
                      yAxisID: 'yParts',
                      partDetails: monthlyPartDetails,
                      monthlyCategoryTotals
                    }
                  ];

                  // Build company-based cash datasets
                  type MonthlyBreakdown = {
                    single: number; twoStart: number; twoEnd: number; advance: number; progress: number; settlement: number;
                    singleBase: number; twoStartBase: number; twoEndBase: number; advanceBase: number; progressBase: number; settlementBase: number;
                  };

                  type CompanyAgg = {
                    id: string;
                    name: string;
                    cash: number[];
                    breakdown: MonthlyBreakdown[];
                    partDetails: Array<Array<{ projectId: number; partId: number; title: string; amount: number }>>;
                    total: number;
                  };

                  const ensureAgg = (map: Record<string, CompanyAgg>, id: string, name: string): CompanyAgg => {
                    if (!map[id]) {
                      map[id] = {
                        id,
                        name,
                        cash: Array(12).fill(0),
                        breakdown: Array.from({ length: 12 }, () => ({
                          single: 0, twoStart: 0, twoEnd: 0, advance: 0, progress: 0, settlement: 0,
                          singleBase: 0, twoStartBase: 0, twoEndBase: 0, advanceBase: 0, progressBase: 0, settlementBase: 0
                        })),
                        partDetails: Array.from({ length: 12 }, () => []),
                        total: 0
                      };
                    }
                    return map[id];
                  };

                  const companies: Record<string, CompanyAgg> = {};

                  projects.forEach(project => {
                    const companyId = (project.building?.company?.id ?? 'onbekend').toString();
                    const companyName = project.building?.company?.name ?? 'Onbekend bedrijf';
                    const agg = ensureAgg(companies, companyId, companyName);

                    (project.parts ?? []).forEach((part: any) => {
                      const budget = getPartBudget(part);
                      if (!budget || budget <= 0) return;
                      const psRaw = part?.plannedStartDate ? new Date(part.plannedStartDate) : null;
                      const peRaw = part?.plannedEndDate ? new Date(part.plannedEndDate) : null;
                      if (!psRaw || !peRaw || peRaw < psRaw) return;

                      const ps = clampToYear(psRaw, year);
                      const pe = clampToYear(peRaw, year);
                      const startM = ps.getFullYear() === year ? ps.getMonth() : 0;
                      const endM = pe.getFullYear() === year ? pe.getMonth() : 11;
                      const span = endM - startM + 1;
                      if (span <= 0) return;

                      // Cash distribution
                      const dist = distributeBudgetByMonths(part, year);
                      dist.forEach((amount: number, idx: number) => {
                        if (amount > 0) {
                          agg.cash[idx] += amount;
                          agg.total += amount;
                          agg.partDetails[idx].push({ projectId: project.id, partId: part.id, title: part.title, amount });
                        }
                      });

                      // Categorized breakdown per month
                      if (span === 1) {
                        agg.breakdown[startM].single += budget;
                        agg.breakdown[startM].singleBase += budget;
                        return;
                      }
                      if (span === 2) {
                        agg.breakdown[startM].twoStart += budget * 0.5;
                        agg.breakdown[startM].twoStartBase += budget;
                        agg.breakdown[endM].twoEnd += budget * 0.5;
                        agg.breakdown[endM].twoEndBase += budget;
                        return;
                      }

                      // > 2 months: 30/40/30
                      agg.breakdown[startM].advance += budget * 0.3;
                      agg.breakdown[startM].advanceBase += budget;
                      const middleMonths = span - 2;
                      if (middleMonths > 0) {
                        const per = (budget * 0.4) / middleMonths;
                        for (let m = startM + 1; m <= endM - 1; m++) {
                          agg.breakdown[m].progress += per;
                          agg.breakdown[m].progressBase += budget;
                        }
                      }
                      agg.breakdown[endM].settlement += budget * 0.3;
                      agg.breakdown[endM].settlementBase += budget;
                    });
                  });

                  // Sort companies by total descending for consistent colors/legend
                  const sortedCompanies = Object.values(companies).sort((a, b) => b.total - a.total);

                  const colors = [
                    'rgba(91, 115, 4, 0.9)',     // moss-primary
                    'rgba(234, 129, 98, 0.9)',   // brick-primary
                    'rgba(245, 221, 127, 0.9)',  // zacht geel
                    'rgba(184, 237, 217, 0.9)',  // muntgroen
                    'rgba(40, 63, 17, 0.9)',     // donkergroen
                    'rgba(91, 115, 4, 0.7)',
                    'rgba(234, 129, 98, 0.7)',
                    'rgba(245, 221, 127, 0.7)',
                    'rgba(184, 237, 217, 0.7)',
                    'rgba(49, 64, 59, 0.7)'      // neutral-primary
                  ];

                  sortedCompanies.forEach((comp, idx) => {
                    const monthlyCompanyCategoryTotals = comp.breakdown.map(b => {
                      const items: { key: string; label: string; amount: number; totalRef?: number }[] = [];
                      if (b.single > 0) items.push({ key: 'single', label: '100% in één maand', amount: b.single, totalRef: b.singleBase });
                      if (b.twoStart > 0) items.push({ key: 'two-start', label: 'Voorschot (2 maanden) 50% van totaalbudget', amount: b.twoStart, totalRef: b.twoStartBase });
                      if (b.advance > 0) items.push({ key: 'advance', label: 'Voorschot 30% van totaalbudget', amount: b.advance, totalRef: b.advanceBase });
                      if (b.progress > 0) items.push({ key: 'progress', label: 'Voortgang pro rata (40% verdeeld over tussenliggende maanden)', amount: b.progress, totalRef: b.progressBase });
                      if (b.twoEnd > 0) items.push({ key: 'two-end', label: 'Afrekening (2 maanden) 50% van totaalbudget', amount: b.twoEnd, totalRef: b.twoEndBase });
                      if (b.settlement > 0) items.push({ key: 'settlement', label: 'Afrekening 30% van totaalbudget', amount: b.settlement, totalRef: b.settlementBase });
                      return items;
                    });

                    // Sort details per month by amount desc and cap later in tooltip
                    const sortedDetails = comp.partDetails.map(list => list.sort((a, b) => b.amount - a.amount));

                    datasets.push({
                      label: comp.name,
                      data: comp.cash,
                      backgroundColor: colors[idx % colors.length],
                      yAxisID: 'yCash',
                      stack: 'cash',
                      monthlyCategoryTotals: monthlyCompanyCategoryTotals,
                      partDetails: sortedDetails
                    });
                  });

                  return datasets;
                })()
            };

            const options = {
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index' as const, intersect: false },
              plugins: {
                legend: { position: 'top' as const },
                title: { display: false },
                tooltip: {
                  callbacks: {
                    label: function (context: any) {
                      if (context.dataset?.label === 'Onderdelen') {
                        return `Onderdelen: ${context.raw}`;
                      }
                      return `Uitbetaling: ${formatCurrency(Number(context.raw))}`;
                    },
                    afterBody: function (contextArr: any[]) {
                      // Compact category breakdown for the hovered month (no totals to reduce noise)
                      if (!contextArr || contextArr.length === 0) return [];
                      const ctx = contextArr[0];
                      const monthIdx = ctx.dataIndex;
                      const categories = ctx.dataset?.monthlyCategoryTotals?.[monthIdx] || [];
                      if (!categories.length) return [];
                      const header = 'Verdeling cash:';
                      const lines = categories.map((c: any) => `  - ${c.label}: ${formatCurrency(c.amount)}`);
                      return [header, ...lines];
                    },
                    afterLabel: function (context: any) {
                      // For the cash datasets, show top projects (aggregated), not individual parts
                      if (context.dataset?.yAxisID !== 'yCash') return [];
                      const details = context.dataset?.partDetails?.[context.dataIndex] || [];
                      if (!details.length) return [];
                      // Aggregate by project
                      const byProject: Record<string, { id: number; amount: number }> = {};
                      for (const d of details) {
                        const key = d.projectId.toString();
                        if (!byProject[key]) byProject[key] = { id: d.projectId, amount: 0 };
                        byProject[key].amount += Number(d.amount) || 0;
                      }
                      const aggregated = Object.values(byProject)
                        .sort((a, b) => b.amount - a.amount);
                      const top = aggregated.slice(0, 5);
                      const lines = top.map(p => `  - RENO-${p.id}: ${formatCurrency(p.amount)}`);
                      if (aggregated.length > 5) {
                        lines.push(`  … +${aggregated.length - 5} meer projecten`);
                      }
                      return lines;
                    }
                  }
                }
              },
              scales: {
                yParts: {
                  type: 'linear' as const,
                  position: 'left' as const,
                  beginAtZero: true,
                  suggestedMax: 10,
                  ticks: { color: '#31403B' },
                  grid: { color: 'rgba(49, 64, 59, 0.1)' }
                },
                yCash: {
                  type: 'linear' as const,
                  position: 'right' as const,
                  beginAtZero: true,
                    stacked: true,
                  ticks: {
                    color: '#31403B',
                    callback: (value: any) => formatCurrency(Number(value))
                  },
                  grid: { drawOnChartArea: false }
                },
                x: {
                  ticks: { color: '#31403B' },
                  grid: { color: 'rgba(49, 64, 59, 0.1)' }
                }
              }
            } as const;

            return (
              <div className="h-64">
                <Bar data={data as any} options={options as any} />
              </div>
            );
          })()}
        </CardBody>
      </Card>

      {/* Extra inzicht: Begroot bedrag per renovatielabel */}
      <Card className="p-4">
        <CardHeader className="pb-2">
          <div className="text-sm font-medium text-neutral-primary">Begroot per renovatielabel</div>
        </CardHeader>
        <CardBody>
          {(() => {
            ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, ChartJsTooltip, Legend);
            // Aggregate assigned budget per label name (budget from investment item assignments)
            const byLabel: Record<string, { total: number; parts: Array<{ projectId: number; partId: number; title: string; amount: number }> }> = {};
            (projects || []).forEach(project => {
              (project.parts || []).forEach((part: any) => {
                const amount = (part.investmentAssignments || []).reduce((s: number, a: any) => s + Number(a.assignedAmount || 0), 0);
                if (!amount) return;
                const labelName = part.label?.name || 'Geen label';
                if (!byLabel[labelName]) byLabel[labelName] = { total: 0, parts: [] };
                byLabel[labelName].total += amount;
                byLabel[labelName].parts.push({ projectId: (project as any).id, partId: part.id, title: part.title, amount });
              });
            });
            const labels = Object.keys(byLabel);
            const dataValues = labels.map(l => byLabel[l].total);
            // Sort desc for readability
            const pairs = labels.map((l, i) => ({ l, v: dataValues[i] })).sort((a, b) => b.v - a.v);
            const sortedLabels = pairs.map(p => p.l);
            const sortedValues = pairs.map(p => p.v);
            const labelPartDetails = sortedLabels.map(l => (byLabel[l]?.parts || []).sort((a, b) => b.amount - a.amount));
            const data = {
              labels: sortedLabels,
              datasets: [{
                label: 'Begroot',
                data: sortedValues,
                backgroundColor: 'rgba(91, 115, 4, 0.8)',
                labelPartDetails
              }]
            };
            const options = {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    afterLabel: function(ctx: any) {
                      const details = ctx?.dataset?.labelPartDetails?.[ctx.dataIndex] || [];
                      if (!details.length) return [] as any;
                      const top = details.slice(0, 6);
                      const lines = top.map((d: any) => `  - RENO-${d.projectId}-O${d.partId}: ${d.title}`);
                      if (details.length > 6) lines.push(`  … +${details.length - 6} onderdelen`);
                      return lines;
                    }
                  }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: { color: '#31403B', callback: (v: any) => formatCurrency(Number(v)) },
                  grid: { color: 'rgba(49,64,59,0.1)' }
                },
                x: { ticks: { color: '#31403B' }, grid: { display: false } }
              },
              interaction: { intersect: false, mode: 'index' as const },
              pluginsExtra: {}
            } as any;
            return (
              <div className="h-64">
                <Bar data={data as any} options={options} />
              </div>
            );
          })()}
        </CardBody>
      </Card>

      {/* Matrix: Begroot per project x label, met hover details en uitklapbare rij */}
      <Card className="p-4">
        <CardHeader className="pb-2">
          <div className="text-sm font-medium text-neutral-primary">Projecten × Renovatielabels (begroot)</div>
        </CardHeader>
        <CardBody>
          {(() => {
            // Verzamel alle unieke labelnamen (stabiele volgorde: sorteer op totaal aflopend)
            // Budget now comes from investment item assignments
            const labelTotals: Record<string, number> = {};
            projects.forEach(p => (p.parts || []).forEach((part: any) => {
              const amt = (part.investmentAssignments || []).reduce((s: number, a: any) => s + Number(a.assignedAmount || 0), 0);
              if (!amt) return;
              const ln = part.label?.name || 'Geen label';
              labelTotals[ln] = (labelTotals[ln] || 0) + amt;
            }));
            const allLabels = Object.keys(labelTotals).sort((a, b) => (labelTotals[b] - labelTotals[a]));
            const grandTotal = allLabels.reduce((s, l) => s + (labelTotals[l] || 0), 0);

            type CellDetail = { partId: number; title: string; amount: number };
            const projectRows = projects.map(p => {
              const byLabel: Record<string, { total: number; parts: CellDetail[] }> = {};
              (p.parts || []).forEach((part: any) => {
                const amt = (part.investmentAssignments || []).reduce((s: number, a: any) => s + Number(a.assignedAmount || 0), 0);
                if (!amt) return;
                const ln = part.label?.name || 'Geen label';
                if (!byLabel[ln]) byLabel[ln] = { total: 0, parts: [] };
                byLabel[ln].total += amt;
                byLabel[ln].parts.push({ partId: part.id, title: part.title, amount: amt });
              });
              return { p, byLabel };
            });

            const [expanded, setExpanded] = React.useState<Record<number, boolean>>({});
            const toggle = (id: number) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

            return (
              <div className="w-full overflow-auto border border-neutral-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-neutral-secondary-mid font-medium sticky left-0 bg-neutral-50">Project</th>
                      {allLabels.map(label => (
                        <th key={label} className="px-3 py-2 text-left text-neutral-secondary-mid font-medium">{label}</th>
                      ))}
                      <th className="px-3 py-2 text-left text-neutral-secondary-mid font-medium">Totaal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projectRows.map(({ p, byLabel }) => (
                      <React.Fragment key={p.id}>
                        <tr className="border-t border-neutral-200 hover:bg-neutral-50">
                          <td className="px-3 py-2 sticky left-0 bg-white">
                            <button onClick={() => toggle(p.id)} className="mr-2 text-moss-primary">{expanded[p.id] ? '−' : '+'}</button>
                            <Link to={`/renovation/${p.id}?buildingId=${p.building?.id}`} className="text-moss-primary hover:underline">RENO-{p.id} {p.title}</Link>
                          </td>
                          {allLabels.map(label => {
                            const cell = byLabel[label];
                            const value = cell?.total || 0;
                            const details = (cell?.parts || []).sort((a, b) => b.amount - a.amount);
                            return (
                              <td key={label} className="px-3 py-2">
                                {value > 0 ? (
                                  <Tooltip content={
                                    <div className="text-[11px] max-w-xs">
                                      <div className="font-medium mb-1">Onderdelen:</div>
                                      <ul className="list-disc pl-4 space-y-0.5">
                                        {details.slice(0, 6).map(d => (
                                          <li key={d.partId}>RENO-{p.id}-O{d.partId}: {d.title}</li>
                                        ))}
                                        {details.length > 6 && (
                                          <li>… +{details.length - 6} onderdelen</li>
                                        )}
                                      </ul>
                                    </div>
                                  }>
                                    <span className="font-medium text-neutral-primary">{formatCurrency(value)}</span>
                                  </Tooltip>
                                ) : (
                                  <span className="text-neutral-400">-</span>
                                )}
                              </td>
                            );
                          })}
                          {/* Row total */}
                          <td className="px-3 py-2 font-medium text-neutral-primary">
                            {formatCurrency(allLabels.reduce((s, l) => s + (byLabel[l]?.total || 0), 0))}
                          </td>
                        </tr>
                        {expanded[p.id] && (
                          <tr className="bg-neutral-50 border-t border-neutral-200">
                            <td className="px-3 py-2 sticky left-0 bg-neutral-50" colSpan={2 + allLabels.length}>
                              <div className="flex flex-wrap gap-2">
                                {(p.parts || []).map((part: any) => {
                                  const amt = (part.investmentAssignments || []).reduce((s: number, a: any) => s + Number(a.assignedAmount || 0), 0);
                                  const ln = part.label?.name || 'Geen label';
                                  if (!amt) return null;
                                  return (
                                    <Chip key={part.id} size="sm" className="bg-moss-primary/10 text-moss-primary" title={`${ln} • ${formatCurrency(amt)}`}>
                                      <span className="font-medium">RENO-{p.id}-O{part.id}</span>
                                      <span className="mx-1">•</span>
                                      <span className="truncate max-w-[160px]">{part.title}</span>
                                      <span className="mx-1">•</span>
                                      <span>{ln}</span>
                                      <span className="mx-1">•</span>
                                      <span>{formatCurrency(amt)}</span>
                                    </Chip>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {/* Column totals row */}
                    <tr className="border-t border-neutral-200 bg-neutral-50">
                      <td className="px-3 py-2 sticky left-0 bg-neutral-50 font-medium text-neutral-primary">Totaal</td>
                      {allLabels.map(label => (
                        <td key={`tot-${label}`} className="px-3 py-2 font-medium text-neutral-primary">{formatCurrency(labelTotals[label] || 0)}</td>
                      ))}
                      <td className="px-3 py-2 font-semibold text-neutral-primary">{formatCurrency(grandTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })()}
        </CardBody>
      </Card>

          {/* Workload per user per month – compacte overzichten per maand */}
          <Card className="p-4">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between w-full">
                <div className="text-sm font-medium text-neutral-primary">Werkbelasting per gebruiker (per maand)</div>
                <div className="text-xs text-neutral-500">Aantal projectonderdelen met planning in de maand</div>
              </div>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {monthLabels.map((label, mIdx) => {
                  // Build list of users with counts for this month
                  const monthUsers = workload.usersList
                    .map(u => ({ ...u, count: u.monthly[mIdx] }))
                    .filter(u => u.count > 0)
                    .sort((a, b) => b.count - a.count);
                  const top = monthUsers.slice(0, 8);
                  const othersCount = monthUsers.slice(8).reduce((s, u) => s + u.count, 0);
                  return (
                    <div key={mIdx} className="rounded-lg border border-neutral-200 p-3 bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-neutral-500">{label}</span>
                        <Chip size="sm" className="bg-moss-primary/10 text-moss-primary">{monthUsers.reduce((s,u)=>s+u.count,0)}</Chip>
                      </div>
                      {top.length === 0 ? (
                        <div className="text-xs text-neutral-500">Geen onderdelen</div>
                      ) : (
                        <ul className="space-y-1">
                          {top.map(u => (
                            <li key={u.id} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                {u.image ? (
                                  <Avatar size="sm" src={u.image} />
                                ) : (
                                  <div className="w-6 h-6 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 text-[10px]">?</div>
                                )}
                                <span className="truncate max-w-[140px]">{u.name}</span>
                              </div>
                              <span className="font-medium text-neutral-primary">{u.count}</span>
                            </li>
                          ))}
                          {othersCount > 0 && (
                            <li className="flex items-center justify-between text-xs text-neutral-600">
                              <span>Overig</span>
                              <span className="font-medium">{othersCount}</span>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardBody>
          </Card>

      {/* Month header */}
      <div className="rounded-lg border border-neutral-200">
        <div className="grid grid-cols-12 text-xs font-medium text-neutral-secondary-mid bg-neutral-50">
          {monthGrid.map(m => (
            <div key={m.index} className="px-2 py-2 text-center border-r last:border-r-0 border-neutral-200">
              {m.label}
            </div>
          ))}
        </div>
      </div>

      {/* Projects */}
      <div className="space-y-4">
        {projects.map(project => {
          const timeline = getProjectTimeline(project, year);
          const totalBudget = getProjectBudget(project);
          const buildingId = project.building?.id;
          const assignedUid = localAssignments[project.id] ?? (project as any).responsibleUserId;
          const assignedUser: any = users.find((u: any) => u.id === assignedUid);
          const tooltipText = assignedUser ? `${assignedUser.first_name} ${assignedUser.last_name}` : 'Geen verantwoordelijke';
          const continuationInfo = getProjectContinuationInfo(project, year);

          return (
            <Card key={project.id} className="border border-neutral-200 shadow-sm">
              <CardHeader className="flex flex-col items-start gap-2">
                <div className="w-full flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Chip size="sm" className="bg-neutral-100 text-neutral-primary">{buildingId}</Chip>
                    <Link to={`/renovation/${project.id}?buildingId=${buildingId}`} className="text-moss-primary hover:underline font-medium">
                      RENO-{project.id} {project.title}
                    </Link>
                    {continuationInfo && (
                      <Tooltip content={continuationInfo.message}>
                        <Chip 
                          size="sm" 
                          className="bg-brick-primary/10 text-brick-primary border border-brick-primary/30"
                        >
                          Doorlopend sinds {continuationInfo.startYear}
                        </Chip>
                      </Tooltip>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-neutral-secondary-mid">
                      {timeline?.start ? format(timeline.start, "dd/MM/yyyy") : "?"} → {timeline?.end ? format(timeline.end, "dd/MM/yyyy") : "?"}
                    </div>
                    <div className="text-sm font-semibold text-neutral-primary">
                      Totaal budget: {formatCurrency(totalBudget)}
                    </div>
                    {/* Responsible avatar with quick assign */}
                    <Tooltip content={tooltipText}>
                      <button
                        className="rounded-full border border-neutral-primary/40 p-0.5 hover:bg-neutral-50"
                        onClick={(e) => { e.preventDefault(); setAssignProjectId(project.id); setAssignModalOpen(true); }}
                      >
                        {assignedUser?.image_url ? (
                          <Avatar size="sm" src={assignedUser.image_url} />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 text-sm">?</div>
                        )}
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {/* Timeline row */}
                <div className="w-full">
                  <div className="grid grid-cols-12 gap-[2px] mt-2">
                    {monthGrid.map(m => {
                      const inRange = timeline ? m.index >= timeline.startMonth && m.index <= timeline.endMonth : false;
                      return (
                        <div
                          key={m.index}
                          className={
                            `h-3 rounded-xs ${inRange ? 'bg-moss-primary' : 'bg-neutral-100'} `
                          }
                          aria-label={`Maand ${m.label} ${inRange ? 'gepland' : 'niet gepland'}`}
                        />
                      );
                    })}
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                <div className="flex flex-col gap-3">
                  <div className="text-sm font-medium text-neutral-primary">Onderdelen</div>
                  <div className="flex flex-wrap gap-2">
                    {(project.parts ?? []).map(part => {
                      const partBudget = getPartBudget(part as any);
                      const isBudgeted = partBudget > 0;
                      return (
                        <Tooltip
                          key={part.id}
                          content={isBudgeted ? `Begroot: ${formatCurrency(partBudget)}` : "Geen budget toegewezen"}
                        >
                          <Chip
                            className={
                              `cursor-default ${isBudgeted ? 'bg-moss-primary text-white' : 'bg-brick-primary text-white'} `
                            }
                            variant="solid"
                            size="md"
                          >
                            <span className="font-medium">RENO-{project.id}-O{part.id}</span>
                            <span className="mx-2">•</span>
                            <span>{part.title}</span>
                              {(part.plannedStartDate || part.plannedEndDate) && (
                                <>
                                  <span className="mx-2">•</span>
                                  <span>{part.plannedStartDate ? format(new Date(part.plannedStartDate), 'dd/MM/yyyy') : '?'} → {part.plannedEndDate ? format(new Date(part.plannedEndDate), 'dd/MM/yyyy') : '?'}</span>
                                </>
                              )}
                            <span className="mx-2">•</span>
                            <span>{formatCurrency(partBudget)}</span>
                          </Chip>
                        </Tooltip>
                      );
                    })}
                  </div>
                  {(() => {
                    const sched = getProjectCashSchedule(project, year);
                    const sectionClass = "text-[12px] text-neutral-600";
                    const pill = (label: string, m: number, amount: number) => (
                      <div key={`${label}-${m}`} className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-sm px-2 py-1">
                        <span className="text-neutral-primary font-medium">{label}</span>
                        <span className="text-neutral-secondary-mid">{monthLabels[m]}</span>
                        <span className="text-moss-primary font-semibold">{formatCurrency(amount)}</span>
                      </div>
                    );
                    const hasData = sched.advance.length || sched.progress.length || sched.settlement.length || sched.single.length || sched.twoStart.length || sched.twoEnd.length;
                    if (!hasData) return null;
                    return (
                      <div className="mt-2 space-y-2">
                        <div className="text-sm font-medium text-neutral-primary">Cashplanning (dit jaar)</div>
                        <div className={sectionClass}>
                          {/* Order: voorschot (incl 2m start) -> pro rata -> afrekening (incl 2m end) */}
                          {sched.advance.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              <span className="text-neutral-primary font-medium mr-1">Voorschot 30%:</span>
                              {sched.advance.map(([m, amount]) => pill('30%', m, amount))}
                            </div>
                          )}
                          {sched.twoStart.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className="text-neutral-primary font-medium mr-1">Voorschot (2 maanden) 50%:</span>
                              {sched.twoStart.map(([m, amount]) => pill('50%', m, amount))}
                            </div>
                          )}
                          {sched.progress.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className="text-neutral-primary font-medium mr-1">Voortgang pro rata:</span>
                              {sched.progress.map(([m, amount]) => {
                                const baseEntry = sched.progressBase.find(([mm]) => mm === m);
                                const base = baseEntry ? baseEntry[1] : 0;
                                const pct = base > 0 ? Math.round((amount / base) * 1000) / 10 : 0; // one decimal
                                return pill(`${pct}%`, m, amount);
                              })}
                            </div>
                          )}
                          {sched.settlement.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className="text-neutral-primary font-medium mr-1">Afrekening 30%:</span>
                              {sched.settlement.map(([m, amount]) => pill('30%', m, amount))}
                            </div>
                          )}
                          {sched.twoEnd.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className="text-neutral-primary font-medium mr-1">Afrekening (2 maanden) 50%:</span>
                              {sched.twoEnd.map(([m, amount]) => pill('50%', m, amount))}
                            </div>
                          )}
                          {sched.single.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className="text-neutral-primary font-medium mr-1">100% in één maand:</span>
                              {sched.single.map(([m, amount]) => pill('100%', m, amount))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PlanningView;

